import { makeStyles } from '@mui/styles';
import { Button, Grid, Loader, MenuButton, Typography } from 'apollo-react';
import { neutral4 } from 'apollo-react/colors';
import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { extractRowsForPayload, validateConfigDatasourcXml } from './configDatasourceUtils';
import ConfigureDataSourcesTable from './ConfigureDataSourcesTable';
import ExternalDataDisplayModal from './ExternalDataDisplayModal';
import ImportConfigDatasourceModal from './ImportConfigDatasourceModal';
import {
  addRow,
  deleteRow,
  importRows,
  setDeleteRowId,
  setInitialRows,
  setRows,
  setSource,
  setStatus,
  updateRow,
} from '../../actions/ConfigDatasourcesActions';
import { handleErrors } from '../../actions/ErrorActions';
import { addSuccess } from '../../actions/SuccessActions';
import {
  deletConfigDatasource,
  exportXMLConfigDataSource,
  getConfigDatasources,
  saveConfigDatasource,
} from '../../api/config-datasources';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import Layout from '../../components/Layout';
import { CONFIGURE_DATA_SOURCE } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getConfigureDatasources } from '../../selectors/ConfigureDataSourcesSelectors';
import { invokeBlobFileDownloading } from '../../utils/commonUtils';
import { getContentDispositionFile } from '../../utils/savedSearchUtils';

const CONNECTION_STRING = 'USE_SMARTSOLVE_CONNECTION';
const FILE_NAME = 'ExternalDataSources';
const FILE_EXTENSION = 'xml';

const useStyles = makeStyles({
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    letterSpacing: 'normal',
    color: '#000',
  },
  loadingContainer: {
    position: 'absolute',
    width: '95%',
    height: '80%',
  },
  loaderTitle: {
    margin: 35,
    color: neutral4,
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  actionButton: {
    marginRight: 20,
  },
  paddingHeader: {
    padding: '13px 20px',
  },
  tableTopMargin: {
    marginTop: 15,
  },
  onResizeNoScrollInButton: {
    overflow: 'hidden',
  },
});

export const ConfigureExternalData = () => {
  const {
    initialRows,
    rows,
    deleteRowId,
    source,
    status: {
      isLoading,
      hasChanges,
      hasEmptyFields,
      isImportModalOpen,
      isDeleteModalOpen,
      isExternalDataModalOpen,
    },
  } = useSelector(getConfigureDatasources);
  const classes = useStyles();
  const translateString = useTranslation();
  const dispatch = useDispatch();

  const title = translateString('Data Sources');

  const handleImportModalOpen = useCallback(() => {
    dispatch(setStatus({ isImportModalOpen: !isImportModalOpen }));
  }, [dispatch, isImportModalOpen]);

  const importConfigDatasourceFile = useCallback(
    async (xmlFileToJsonData) => {
      try {
        if (xmlFileToJsonData.length > 0) {
          const updatedRows = rows.map((row) => {
            const importedRow = xmlFileToJsonData.find((imported) => imported.id === row.id);
            return importedRow ? { ...importedRow, imported: true } : row;
          });

          const newRows = xmlFileToJsonData
            .filter((imported) => !rows.some((row) => row.id === imported.id))
            .map((newRow) => ({ ...newRow, id: newRow.id || uuidv4(), imported: true }));
          const allRows = [...newRows, ...updatedRows];

          dispatch(importRows(allRows));
          dispatch(addSuccess({ message: 'Data Sources Imported Successfully' }));
        }
      } catch (error) {
        dispatch(handleErrors(error));
      }
    },
    [dispatch, rows]
  );

  const handleExportConfigDataSource = async () => {
    try {
      const { data: apiData, headers } = await exportXMLConfigDataSource();

      if (apiData instanceof Blob && headers['content-type'] === 'application/json') {
        const text = await apiData.text();
        const json = JSON.parse(text);

        if (json.hasError) {
          dispatch(handleErrors(json.errors));
        }
      } else {
        const contentDisposition = headers['content-disposition'];
        const contentDispositionFile = getContentDispositionFile(contentDisposition);
        const fileExtension = contentDispositionFile?.extension || FILE_EXTENSION;
        invokeBlobFileDownloading(apiData, `${FILE_NAME}.${fileExtension}`);
      }
    } catch (error) {
      dispatch(handleErrors(error));
    }
  };

  useEffect(() => {
    document.title = title || 'Data Sources';
  }, [title]);

  useEffect(() => {
    const loadConfigDataSources = async () => {
      dispatch(setStatus({ isLoading: true }));
      const response = await getConfigDatasources();
      if (!response.hasError) {
        dispatch(setInitialRows(response.resource));
        dispatch(setRows(response.resource || []));
      } else {
        dispatch(handleErrors(response.errors));
      }
      dispatch(setStatus({ isLoading: false }));
    };

    loadConfigDataSources();
  }, [dispatch]);

  useEffect(() => {
    const changesDetected = JSON.stringify(rows) !== JSON.stringify(initialRows);
    dispatch(setStatus({ hasChanges: changesDetected }));

    const emptyFieldsDetected = rows.some((row) =>
      Object.values(row).some((value) => value === '')
    );
    dispatch(setStatus({ hasEmptyFields: emptyFieldsDetected }));
  }, [rows, initialRows, dispatch]);

  const onSave = async () => {
    try {
      dispatch(setStatus({ isLoading: true }));
      const payload = extractRowsForPayload(rows, initialRows);
      const response = await saveConfigDatasource(payload);
      if (!response.hasError) {
        dispatch(setRows(response.resource || []));
        dispatch(setInitialRows(response.resource));
        dispatch(addSuccess({ message: translateString('Data Sources Saved Successfully') }));
        dispatch(setStatus({ hasChanges: false }));
      } else {
        dispatch(handleErrors(response.errors));
      }
    } catch (error) {
      dispatch(handleErrors([error.message]));
    } finally {
      dispatch(setStatus({ isLoading: false }));
    }
  };

  const onDelete = (id) => {
    dispatch(setDeleteRowId(id));
    dispatch(setStatus({ isDeleteModalOpen: true }));
  };

  const onOpenExternalDataSource = (source) => {
    const getSourceFromSavedRow = initialRows.find((row) => row.id === source.id);
    dispatch(setSource(getSourceFromSavedRow));
    dispatch(setStatus({ isExternalDataModalOpen: true }));
  };

  const confirmDelete = async () => {
    const rowToDelete = rows.find((row) => row.id === deleteRowId);

    if (!rowToDelete.imported && !rowToDelete.isNewRow) {
      try {
        const response = await deletConfigDatasource(rowToDelete.id);
        if (!response.hasError) {
          dispatch(addSuccess({ message: translateString('Data Source Deleted Successfully') }));
          dispatch(deleteRow(deleteRowId));
        } else {
          dispatch(handleErrors(response.errors));
        }
      } catch (error) {
        dispatch(handleErrors([error.message]));
      }
    } else {
      dispatch(deleteRow(deleteRowId));
    }

    dispatch(setStatus({ isDeleteModalOpen: false }));
    dispatch(setDeleteRowId(null));
  };

  const editRow = (id, key, value) => {
    dispatch(updateRow(id, key, value));

    const changesDetected = JSON.stringify(rows) !== JSON.stringify(initialRows);
    dispatch(setStatus({ hasChanges: changesDetected }));

    const emptyFieldsDetected = rows.some((row) =>
      Object.values(row).some((value) => value === '')
    );
    dispatch(setStatus({ hasEmptyFields: emptyFieldsDetected }));
  };

  const updateQueryInSourceTableRow = (source, newQuery) => {
    for (const item of rows) {
      if (item.id === source.id) {
        item.query = newQuery;
        return item;
      }
    }
    return null;
  };

  const onAddRow = () => {
    const newRow = {
      id: uuidv4(),
      source: '',
      description: '',
      connection: CONNECTION_STRING,
      query: '',
      selectValueColumns: '',
      isNewRow: true,
    };
    dispatch(addRow(newRow));
  };

  const menuItems = [
    { text: translateString('Import'), onClick: handleImportModalOpen },
    { text: translateString('Export'), onClick: handleExportConfigDataSource },
  ];

  const ActionButton = (
    <>
      <MenuButton
        buttonText={translateString('Action')}
        size="medium"
        menuItems={menuItems}
        style={{ marginRight: 8 }}
      />

      <Button
        size="medium"
        variant="primary"
        disabled={!hasChanges || hasEmptyFields || rows.length === 0}
        onClick={onSave}
        className={classes.onResizeNoScrollInButton}
      >
        {translateString('Save')}
      </Button>
    </>
  );

  return (
    <Layout dashboardLink={CONFIGURE_DATA_SOURCE}>
      {isLoading ? (
        <div className={classes.loadingContainer}>
          <Typography variant="h1" className={classes.loaderTitle}>
            {'Loading Config Datasources...'}
          </Typography>
          <Loader />
        </div>
      ) : (
        <Grid container className={classes.paddingHeader}>
          <Grid item xs={10}>
            <Typography className={classes.title}>{title}</Typography>
          </Grid>
          <Grid item className={classes.buttonWrapper} xs={2}>
            {ActionButton}
          </Grid>
          <Grid item xs={12} className={classes.tableTopMargin}>
            <ConfigureDataSourcesTable
              title={title}
              rows={rows}
              isLoading={isLoading}
              onOpenExternalDataSource={onOpenExternalDataSource}
              onDelete={onDelete}
              editRow={editRow}
              handleImportModalOpen={handleImportModalOpen}
              onSave={onSave}
              onAddRow={onAddRow}
              hasChanges={hasChanges && !hasEmptyFields}
            />
            {isImportModalOpen && (
              <ImportConfigDatasourceModal
                open={isImportModalOpen}
                onClose={handleImportModalOpen}
                validateXml={validateConfigDatasourcXml}
                importConfigDatasourceFile={importConfigDatasourceFile}
              />
            )}
            <DeleteConfirmationModal
              open={isDeleteModalOpen}
              onClose={() => dispatch(setStatus({ isDeleteModalOpen: false }))}
              onDeleteConfirm={confirmDelete}
            />
            <ExternalDataDisplayModal
              open={isExternalDataModalOpen}
              onClose={() => dispatch(setStatus({ isExternalDataModalOpen: false }))}
              source={source}
              updateQueryInSourceTableRow={updateQueryInSourceTableRow}
            />
          </Grid>
        </Grid>
      )}
    </Layout>
  );
};

export default ConfigureExternalData;
